import React, { useState } from 'react';
import styled from 'styled-components';

import { FlashCardQuizBlockData, FlashCard as FlashCardType } from 'api/cms';
import Box from 'components/Box';
import { PrimaryButton, SecondaryButton } from 'components/Button';
import { Image } from 'components/Image';
import { Body, BodyLarge, BodyMicro } from 'components/Text';
import { breakpoints, colors, transition } from 'theme';
import { imageUrl } from 'utils/filestack';

const StyledImage = styled(({ isPage, className, ...props }) => (
  <Box className={className}>
    {/* TODO: Fix this the next time the file is edited. */}
    {/* eslint-disable-next-line react/jsx-props-no-spreading */}
    <Image {...props} />
  </Box>
))`
  img {
    display: block;
    margin: 0 auto;
    max-height: 300px;
    max-width: 100%;
    @supports (object-fit: contain) {
      width: 100%;
      object-fit: contain;
      object-position: center;
    }
  }
`;
const StyledPrimaryButton = styled(PrimaryButton)`
  cursor: pointer;
`;
const StyledSecondaryButton = styled(SecondaryButton)`
  cursor: pointer;
`;
const Card = styled(Box)`
  transform-style: preserve-3d;
`;
// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line react/jsx-props-no-spreading
const CardContainer = styled(({ flipped, ...props }) => <Box {...props} />)`
  perspective: 1000px;
  cursor: pointer;
  ${(props) => `
  &:hover {
    ${Card} {
      transform: rotateY(${props.flipped ? 183 : 3}deg);
    }
    ${PrimaryButton} {
      background: ${colors.turtleGreen};
    }
    ${SecondaryButton} {
      background: white;
      color: ${colors.waterBlue};
    }
    @media (min-width: ${breakpoints[1]}) {
      ${Card} {
        transform: rotateX(${props.flipped ? 183 : 3}deg);
      }
    }
  }`}
`;
// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line react/jsx-props-no-spreading
const CardTile = styled((props) => <Box p={[24, null, 48]} {...props} />)`
  backface-visibility: hidden;
  width: 100%;
  flex: 0 0 auto;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  border-radius: 12px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
`;
const CardBack = styled(CardTile)`
  margin-left: -100%;
  background: ${colors.waterBlue};
  color: white;
`;
const CardFront = styled(CardTile)`
  transform: translateZ(0);
  background: white;
`;

// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line react/function-component-definition
const FlashCard = ({
  blockIndex,
  card: { question, answer, image, instructions },
  index,
  isPage,
}: {
  blockIndex: number;
  card: FlashCardType;
  index: number;
  isPage: boolean;
}) => {
  const [flipped, setFlipped] = useState(false);

  const flip = () => setFlipped(!flipped);

  const Question = question.length > 80 ? Body : BodyLarge;
  const Answer = answer.length > 280 ? Body : BodyLarge;
  return (
    <Box mt={index > 0 && [32, null, 64]}>
      <CardContainer
        width="calc(100% + 8px)"
        mt={24}
        ml="-4px"
        p="4px"
        textAlign="center"
        flipped={flipped}
      >
        <Card
          transform={[
            `translateZ(0) ${flipped ? 'rotateY(180deg)' : ''}`,
            null,
            `translateZ(0) ${flipped ? 'rotateX(180deg)' : ''}`,
          ]}
          display="flex"
          width={1}
          alignItems="stretch"
          transition={`all ${transition}`}
          onClick={flip}
        >
          <CardFront>
            <BodyMicro color={colors.battleshipGrey} mb={18}>
              {index + 1}. {instructions}
            </BodyMicro>
            <StyledImage
              src={imageUrl({
                handle: image.handle,
                mimetype: image.mimetype,
              })}
              alt={question}
              isPage={isPage}
            />
            <Question
              analyticsData={{
                eventName: 'CMS Block',
                eventProperties: {
                  block_position: blockIndex,
                  block_type: 'Flash Card Quiz',
                },
              }}
              mt={36}
              dangerouslySetInnerHTML={{
                __html: question,
              }}
            />
            <Box mt={20}>
              <StyledPrimaryButton
                size="small"
                onClick={flip}
                tabIndex={flipped ? '-1' : '0'}
              >
                {getText('Reveal Answer')}
              </StyledPrimaryButton>
            </Box>
          </CardFront>
          <CardBack transform={['rotateY(180deg)', null, 'rotateX(180deg)']}>
            <Box component={BodyMicro} flex="0 0 auto" opacity={0.7} mb={20}>
              {index + 1}. {instructions}
            </Box>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              height="100%"
              flex="1 1 auto"
            >
              <Answer
                analyticsData={{
                  eventName: 'CMS Block',
                  eventProperties: {
                    block_position: blockIndex,
                    block_type: 'Flash Card Quiz',
                  },
                }}
                dangerouslySetInnerHTML={{
                  __html: answer,
                }}
              />
            </Box>
            <Box component={BodyMicro} flex="0 0 auto" mb={20}>
              <StyledSecondaryButton
                reverse
                size="small"
                onClick={flip}
                tabIndex={flipped ? '0' : '-1'}
              >
                {getText('Go Back')}
              </StyledSecondaryButton>
            </Box>
          </CardBack>
        </Card>
      </CardContainer>
    </Box>
  );
};

// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line react/function-component-definition
const FlashCardQuizBlock = ({
  blockIndex,
  data: { cards },
  isPage,
}: {
  blockIndex: number;
  data: FlashCardQuizBlockData;
  isPage: boolean;
}) => (
  <Box>
    {cards.map((card, index) => (
      <FlashCard
        blockIndex={blockIndex} // eslint-disable-next-line react/no-array-index-key
        key={index}
        card={card}
        index={index}
        isPage={isPage}
      />
    ))}
  </Box>
);

export default FlashCardQuizBlock;
